import i18n from '@/i18n/config'

export const LOCALE_MAP: Record<string, string> = { nb: 'nb-NO', en: 'en-US' }

// All formatters accept an optional IANA `tz`. When given, the instant is
// rendered in that timezone (the event's own zone); otherwise the viewer's
// browser timezone is used. Pair with `tzLabelSuffix` from '@/lib/timezone' to
// append a "(City)" hint when the zone differs from the viewer's.

/**
 * Default locale = the CURRENT app language, resolved per call (default
 * params re-evaluate on every invocation). Callers that re-render on
 * language change therefore get correctly localized dates without having
 * to thread `locale` through manually. Passing an explicit locale still
 * takes precedence.
 */
/** Norwegian in any spelling the app passes («nb-NO», «nb», «no»): the dot clock and dd.mm.yyyy. */
export function isNorwegian(locale: string): boolean {
  return /^(nb|no|nn)(-|$)/i.test(locale)
}

/** Does this locale write the clock with AM/PM («08:00 AM»)? Sizes time columns that must hold it. */
export function twelveHour(locale: string): boolean {
  const cycle = new Intl.DateTimeFormat(locale, { hour: 'numeric' }).resolvedOptions().hourCycle
  return cycle === 'h11' || cycle === 'h12'
}

export function currentLocale(): string {
  return LOCALE_MAP[i18n.language] ?? 'nb-NO'
}

export function formatDate(dateStr: string | null, locale = currentLocale(), tz?: string | null): string {
  if (!dateStr) return ''
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }
  if (tz) opts.timeZone = tz
  return new Date(dateStr).toLocaleDateString(locale, opts)
}

export function formatDateTime(dateStr: string | null, locale = currentLocale(), tz?: string | null): string {
  if (!dateStr) return ''
  // Norwegian: «onsdag 4. november 2026 kl. 18.00». Intl gives «kl. 18:00»; the time goes through
  // `formatClockTime`, the one place the separator is decided (sweep 24.10.2026).
  if (isNorwegian(locale)) {
    const opts: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }
    if (tz) opts.timeZone = tz
    return `${new Date(dateStr).toLocaleDateString(locale, opts)} kl. ${formatClockTime(dateStr, locale, tz)}`
  }
  const opts: Intl.DateTimeFormatOptions = {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }
  if (tz) opts.timeZone = tz
  return new Date(dateStr).toLocaleDateString(locale, opts)
}

/**
 * Numeric date + time for compact scheduling surfaces.
 *
 * Norwegian DS2 contract: `28.08.2026 kl. 22.00`. Other languages stay with
 * their own `Intl` ordering and punctuation instead of inheriting the
 * browser's locale by accident.
 */
export function formatNumericDateTime(
  dateStr: string | null,
  locale = currentLocale(),
  tz?: string | null,
): string {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return ''
  const opts: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }
  if (tz) opts.timeZone = tz

  if (isNorwegian(locale)) {
    opts.hourCycle = 'h23'
    const parts = new Intl.DateTimeFormat(locale, opts).formatToParts(date)
    const part = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((entry) => entry.type === type)?.value ?? ''
    return `${part('day')}.${part('month')}.${part('year')} kl. ${part('hour')}.${part('minute')}`
  }

  return new Intl.DateTimeFormat(locale, opts).format(date)
}

/** Numeric date: `05.10.2026` in Norwegian, the locale's own numeric order otherwise. */
export function formatNumericDate(dateStr: string | null, locale = currentLocale(), tz?: string | null): string {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return ''
  const opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: '2-digit', year: 'numeric' }
  if (tz) opts.timeZone = tz
  if (isNorwegian(locale)) {
    const parts = new Intl.DateTimeFormat(locale, opts).formatToParts(date)
    const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((entry) => entry.type === type)?.value ?? ''
    return `${part('day')}.${part('month')}.${part('year')}`
  }
  return new Intl.DateTimeFormat(locale, opts).format(date)
}

/**
 * Clock time with the Norwegian separator: `14.30` (Intl gives `14:30` for nb-NO). Pair it with
 * «kl.» in the copy. Other locales keep their own form.
 */
export function formatClockTime(dateStr: string | null, locale = currentLocale(), tz?: string | null): string {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  if (Number.isNaN(date.getTime())) return ''
  const opts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' }
  if (tz) opts.timeZone = tz
  if (isNorwegian(locale)) {
    opts.hourCycle = 'h23'
    const parts = new Intl.DateTimeFormat(locale, opts).formatToParts(date)
    const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((entry) => entry.type === type)?.value ?? ''
    return `${part('hour')}.${part('minute')}`
  }
  return new Intl.DateTimeFormat(locale, opts).format(date)
}

export function formatDateShort(dateStr: string | null, locale = currentLocale(), tz?: string | null): string {
  if (!dateStr) return ''
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }
  if (tz) opts.timeZone = tz
  return new Date(dateStr).toLocaleDateString(locale, opts)
}

/** Day and month without the year («4. nov.»), for a list of dates in one season. */
export function formatDayMonth(dateStr: string | null, locale = currentLocale(), tz?: string | null): string {
  if (!dateStr) return ''
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' }
  if (tz) opts.timeZone = tz
  return new Intl.DateTimeFormat(locale, opts).format(new Date(dateStr))
}

/** Clock time: `formatClockTime` under its older name (`14.30` in Norwegian). */
export function formatTime(dateStr: string | null, locale = currentLocale(), tz?: string | null): string {
  return formatClockTime(dateStr, locale, tz)
}

/**
 * Which day-band an instant falls in, relative to `now`. ONE classifier, because
 * three surfaces were each doing this arithmetic themselves: the notifications
 * page's group headers, the notification row's timestamp, and the chat list's
 * clock/weekday/date ladder. Bands that drift apart show a row under «I går»
 * whose own timestamp claims a weekday.
 *
 * Boundaries are local midnights built from date COMPONENTS, not from
 * `getTime() - 86_400_000`. Subtracting a fixed 24 hours is an hour wrong on
 * both Norwegian DST switches, which is enough to misband anything logged near
 * midnight on those two nights.
 */
export type TimestampBand = 'today' | 'yesterday' | 'thisWeek' | 'older'

export function timestampBand(dateStr: string | Date, now: Date = new Date()): TimestampBand {
  const d = dateStr instanceof Date ? dateStr : new Date(dateStr)
  const midnight = (offsetDays: number) =>
    new Date(now.getFullYear(), now.getMonth(), now.getDate() - offsetDays)
  if (d >= midnight(0)) return 'today'
  if (d >= midnight(1)) return 'yesterday'
  if (d >= midnight(7)) return 'thisWeek'
  return 'older'
}

/**
 * A list row's timestamp, complete enough to place on its own.
 *
 * DS2 (`03-pages/{desktop,mobile}/notifications.html`) specifies «I går, 16:22»
 * and «21. feb, 14:00» for anything not from today; the React row rendered a
 * bare `toLocaleTimeString` for EVERY row, so a notification from 22. mai read
 * «14:27» and nothing else — measured on the live page, three months undatable.
 *
 * The day-word for `yesterday` is left to the caller: it owns the translation,
 * and the notifications page already puts «I går» in the group header above.
 *   today · yesterday → 11:57
 *   this week        → man. 15:38
 *   older            → 22. mai 14:27   (+ year when it is not the current one)
 */
export function formatListTimestamp(
  dateStr: string | Date,
  locale = currentLocale(),
  now: Date = new Date(),
): string {
  const d = dateStr instanceof Date ? dateStr : new Date(dateStr)
  if (Number.isNaN(d.getTime())) return ''
  const time = formatClockTime(d.toISOString(), locale)
  const band = timestampBand(d, now)

  // «fre. 03.07» reads as 3 July in Norwegian, so the time always says «kl.», today's too (running
  // and remaining-surfaces reviews, 25.10.2026).
  const at = isNorwegian(locale) ? `kl. ${time}` : time
  if (band === 'today' || band === 'yesterday') return at
  if (band === 'thisWeek') {
    return `${d.toLocaleDateString(locale, { weekday: 'short' })} ${at}`
  }
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' }
  if (d.getFullYear() !== now.getFullYear()) opts.year = 'numeric'
  return `${d.toLocaleDateString(locale, opts)} ${at}`
}

// Day/month/year parts of an instant, evaluated in the given timezone.
function partsInTz(d: Date, locale: string, tz?: string | null) {
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }
  if (tz) opts.timeZone = tz
  const p = new Intl.DateTimeFormat(locale, opts).formatToParts(d)
  const g = (t: string) => p.find((x) => x.type === t)?.value ?? ''
  return { day: g('day'), month: g('month'), year: g('year') }
}

// Stable YYYY-MM-DD key for an instant in the given timezone (same-day check).
function dateKey(d: Date, tz?: string | null): string {
  const opts: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' }
  if (tz) opts.timeZone = tz
  return new Intl.DateTimeFormat('en-CA', opts).format(d)
}

/**
 * Compact date range — used in MetaCard / Overview headers.
 * Norwegian: same-day → "12. mar. 2026"; same-month → "12.–14. mar."; cross-month →
 * "12. mar.–4. apr.". English: "12–14 Mar", "12 Mar – 4 Apr".
 *
 * The day carries its ordinal period in Norwegian on every form (04.10.2026): the
 * multi-day forms printed «4 nov. – 1 des.», which reads as a typo next to the
 * single-day «4. nov.» on the same page.
 */
export function formatDateRange(
  startStr: string | null,
  endStr: string | null,
  locale = currentLocale(),
  tz?: string | null,
  /**
   * Keep the year on multi-day ranges. Off by default because the header call
   * sites sit next to the year already. The /events table (15b/15d) lists
   * several years at once, where a bare "6–7 juni" cannot be placed.
   */
  withYear = false,
): string {
  if (!startStr) return ''
  const start = new Date(startStr)
  const end = endStr ? new Date(endStr) : null
  const s = partsInTz(start, locale, tz)

  if (!end || dateKey(start, tz) === dateKey(end, tz)) {
    return `${s.day}. ${s.month} ${s.year}`
  }

  const e = partsInTz(end, locale, tz)
  const year = withYear ? ` ${e.year}` : ''
  const norwegian = /^(nb|nn|no)\b/i.test(locale)
  if (norwegian) {
    if (s.month === e.month) return `${s.day}.–${e.day}. ${s.month}${year}`
    return `${s.day}. ${s.month}–${e.day}. ${e.month}${year}`
  }
  if (s.month === e.month) {
    return `${s.day}–${e.day} ${s.month}${year}`
  }
  return `${s.day} ${s.month} – ${e.day} ${e.month}${year}`
}

